import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import type { Order } from "../types/order";
import { formatCurrency, formatTimeAgo } from "../utils/formatters";

interface OrderCardProps {
  order: Order;
  type: "new" | "accepted";
  onViewDetails: (order: Order) => void;
  onStatusUpdate: (orderId: string, status: string) => Promise<void>;
}

export const OrderCard = observer(
  ({ order, type, onViewDetails, onStatusUpdate }: OrderCardProps) => {
    const [loadingAction, setLoadingAction] = useState<string | null>(null);

    const handleActionWithLoading = async (action: string, status: string) => {
      setLoadingAction(action);
      try {
        await onStatusUpdate(order.id, status);
      } catch (error) {
        console.error("Error updating status:", error);
      } finally {
        setLoadingAction(null);
      }
    };

    return (
      <div
        onClick={() => !loadingAction && onViewDetails(order)}
        className={`bg-white rounded p-2 mb-2 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow ${
          loadingAction ? "opacity-70 pointer-events-none" : ""
        }`}
      >
        <p className="text-xs font-semibold text-gray-900 flex-1 mr-2 truncate">
          #{order.order_reference}
        </p>

        <div className="flex flex-col mb-1">
          <div className="flex flex-row justify-between items-start mb-1">
            <p className="text-xs text-gray-700 truncate">
              {order.customer_name || "Walk-in Customer"}
            </p>
            <p className="text-xs text-gray-500">
              {formatTimeAgo(order.created_at)}
            </p>
          </div>
          {order.customer_phone && (
            <p className="text-xs text-gray-500 truncate">
              {order.customer_phone}
            </p>
          )}
          <div className="flex flex-row justify-between items-center">
            <p className="text-xs text-gray-500 truncate">
              {order.order_type === "DELIVERY"
                ? "🚚 Delivery"
                : order.order_type === "DINE_IN"
                ? "🍽️ DineIn"
                : "🏪 Pickup"}
            </p>
            <p className="text-xs font-bold text-gray-600">
              {formatCurrency(order.total)}
            </p>
          </div>
        </div>

        {type === "new" && (
          <div className="flex flex-row gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleActionWithLoading("cancel", "CANCELLED");
              }}
              className="bg-[#DC2626] flex-1 py-1.5 rounded disabled:opacity-50"
              disabled={!!loadingAction}
            >
              {loadingAction === "cancel" ? (
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                </div>
              ) : (
                <span className="text-white text-center font-medium text-xs block">
                  Cancel
                </span>
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleActionWithLoading("confirm", "CONFIRMED");
              }}
              className="bg-[#16A34A] flex-1 py-1.5 rounded disabled:opacity-50"
              disabled={!!loadingAction}
            >
              {loadingAction === "confirm" ? (
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                </div>
              ) : (
                <span className="text-white text-center font-medium text-xs block">
                  Confirm
                </span>
              )}
            </button>
          </div>
        )}

        {type === "accepted" && (
          <div className="flex flex-row gap-2">
            {order.status === "CONFIRMED" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionWithLoading("preparing", "PREPARING");
                }}
                className="bg-[#2196F3] flex-1 py-1.5 rounded disabled:opacity-50"
                disabled={!!loadingAction}
              >
                {loadingAction === "preparing" ? (
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <span className="text-white text-center font-medium text-xs block">
                    Start Preparing
                  </span>
                )}
              </button>
            )}

            {order.status === "PREPARING" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionWithLoading("ready", "READY");
                }}
                className="bg-[#9C27B0] flex-1 py-1.5 rounded disabled:opacity-50"
                disabled={!!loadingAction}
              >
                {loadingAction === "ready" ? (
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <span className="text-white text-center font-medium text-xs block">
                    Mark Ready
                  </span>
                )}
              </button>
            )}

            {order.status === "READY" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionWithLoading("complete", "COMPLETED");
                }}
                className="bg-green-600 flex-1 py-1.5 rounded disabled:opacity-50"
                disabled={!!loadingAction}
              >
                {loadingAction === "complete" ? (
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <span className="text-white text-center font-medium text-xs block">
                    Complete
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);
